import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';
import { useI18n } from '../utilities/i18n';
import {
  adminAuthCheck,
  adminAuthComplete,
  adminAuthEmailSendCode,
  adminAuthForgot,
  adminAuthForgotReset,
  adminAuthForgotSms,
  adminAuthForgotVerify,
  adminAuthLogin,
  adminAuthMergeDuplicate,
  adminAuthSmsSend,
  adminAuthSmsVerify,
  adminAuthTelegramSend,
  adminAuthTelegramVerify,
  applyAdminSession,
  getAdminAuthError,
  getAdminAuthRetryAfter,
  isAdminSession,
  isNotAdminMergeOffer,
  normalizeMergeOffer,
} from '../utilities/adminAuthApi';

const customStyles = {
  overlay: {
    backgroundColor: 'rgba(4, 7, 12, 0.92)',
    zIndex: 10000,
  },
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: 0,
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '20px',
    background: 'transparent',
    overflow: 'hidden',
    maxWidth: 'min(480px, calc(100vw - 16px))',
    width: '90%',
  },
};

Modal.setAppElement('#root');

const STEPS = {
  CHECK: 'check',
  PASSWORD: 'password',
  CODE: 'code',
  SETUP: 'setup',
  FORGOT: 'forgot',
  FORGOT_CODE: 'forgot_code',
  FORGOT_RESET: 'forgot_reset',
  MERGE: 'merge',
};

const emptyFlow = () => ({
  step: STEPS.CHECK,
  login: '',
  password: '',
  confirmPassword: '',
  code: '',
  email: '',
  emailCode: '',
  phone: '',
  phoneMasked: '',
  emailMasked: '',
  requestId: '',
  verifyToken: '',
  resetToken: '',
  codeLength: 6,
  resendAfter: 0,
  channel: 'telegram',
  needsEmail: false,
  hasPhone: false,
  mockCode: '',
  mergeOffer: null,
});

function AuthModal({ visible, closeModal, onSuccess, openDocuments }) {
  const { t } = useI18n();
  const dispatch = useDispatch();
  const [flow, setFlow] = useState(emptyFlow);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState('');
  const [resendLeft, setResendLeft] = useState(0);
  const [emailResendLeft, setEmailResendLeft] = useState(0);
  const resendTimer = useRef(null);
  const emailResendTimer = useRef(null);

  const updateFlow = (patch) => setFlow((prev) => ({ ...prev, ...patch }));

  const clearTimer = (ref) => {
    if (ref.current) {
      clearInterval(ref.current);
      ref.current = null;
    }
  };

  const clearResendTimer = () => clearTimer(resendTimer);
  const clearEmailResendTimer = () => clearTimer(emailResendTimer);

  const startCountdown = (ref, setLeft, seconds) => {
    clearTimer(ref);
    const total = Math.max(0, Number(seconds) || 0);
    setLeft(total);
    if (!total) return;
    ref.current = setInterval(() => {
      setLeft((prev) => {
        if (prev <= 1) {
          clearTimer(ref);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startResendCountdown = (seconds) => startCountdown(resendTimer, setResendLeft, seconds);
  const startEmailResendCountdown = (seconds) => startCountdown(emailResendTimer, setEmailResendLeft, seconds);

  useEffect(() => {
    if (!visible) {
      clearResendTimer();
      clearEmailResendTimer();
      setFlow(emptyFlow());
      setBusy(false);
      setError('');
      setHint('');
      setResendLeft(0);
      setEmailResendLeft(0);
    }
    return () => {
      clearResendTimer();
      clearEmailResendTimer();
    };
  }, [visible]);

  const fail = (err, fallbackKey = 'unknownError', { email = false } = {}) => {
    const retry = getAdminAuthRetryAfter(err);
    if (retry) {
      if (email) startEmailResendCountdown(retry);
      else startResendCountdown(retry);
    }
    setError(getAdminAuthError(err, t(fallbackKey)));
  };

  const finishSession = (session) => {
    const data = applyAdminSession(dispatch, session);
    if (!data) {
      setError(t('unknownError'));
      return;
    }
    if (onSuccess) onSuccess(data);
    closeModal();
  };

  const applySendPayload = (data, channel) => {
    updateFlow({
      step: STEPS.CODE,
      channel,
      phone: data.phone || flow.phone,
      requestId: data.request_id || '',
      codeLength: Number(data.code_length) || (channel === 'sms' ? 4 : 6),
      resendAfter: Number(data.resend_after) || 60,
      code: '',
      mockCode: data.mock && data.code ? String(data.code) : '',
    });
    startResendCountdown(data.resend_after || 60);
    setHint(
      data.mock && data.code
        ? t('authMockCode', { code: data.code })
        : (channel === 'sms' ? t('authCodeSentSms') : t('authCodeSentTelegram'))
    );
  };

  const startTelegram = async (loginValue, phoneValue) => {
    const response = await adminAuthTelegramSend({
      login: loginValue,
      ...(phoneValue ? { phone: phoneValue } : {}),
    });
    applySendPayload(response.data || {}, 'telegram');
  };

  const handleCheck = async () => {
    const loginValue = flow.login.trim();
    if (!loginValue) {
      setError(t('authNeedLogin'));
      return;
    }
    setBusy(true);
    setError('');
    setHint('');
    updateFlow({ mergeOffer: null });
    try {
      const response = await adminAuthCheck(loginValue);
      const data = response.data || {};
      updateFlow({
        login: data.login || loginValue,
        phone: data.phone || '',
        phoneMasked: data.phone_masked || '',
        emailMasked: data.email_masked || '',
        hasPhone: Boolean(data.has_phone),
        mergeOffer: null,
      });

      if (data.next === 'password') {
        updateFlow({ step: STEPS.PASSWORD, password: '' });
        return;
      }
      if (data.next === 'telegram') {
        await startTelegram(data.login || loginValue, data.phone);
        return;
      }
      if (data.next === 'phone_required') {
        setError(t('authPhoneRequired'));
        return;
      }
      setError(t('unknownError'));
    } catch (err) {
      if (isNotAdminMergeOffer(err)) {
        const offer = normalizeMergeOffer(err.response.data);
        updateFlow({ mergeOffer: offer });
        setError(offer.message || getAdminAuthError(err, t('authCheckError')));
      } else {
        updateFlow({ mergeOffer: null });
        fail(err, 'authCheckError');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleMergePreview = async () => {
    const loginValue = flow.login.trim();
    if (!loginValue) {
      setError(t('authNeedLogin'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthMergeDuplicate(loginValue, { confirm: false });
      const data = response.data || {};
      if (!data.can_merge && !data.confirm_required) {
        setError(data.message || t('authMergeUnavailable'));
        return;
      }
      updateFlow({
        step: STEPS.MERGE,
        mergeOffer: normalizeMergeOffer({ ...flow.mergeOffer, ...data }),
      });
      setHint('');
    } catch (err) {
      fail(err, 'authMergeError');
    } finally {
      setBusy(false);
    }
  };

  const handleMergeConfirm = async () => {
    const loginValue = flow.login.trim();
    if (!loginValue) {
      setError(t('authNeedLogin'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthMergeDuplicate(loginValue, { confirm: true });
      const data = response.data || {};
      if (!data.ok && !data.merged) {
        setError(data.message || t('authMergeError'));
        return;
      }
      setHint(data.message || t('authMergeSuccess'));
      updateFlow({ step: STEPS.CHECK, mergeOffer: null });
      // После склейки снова идём по обычному adminAuthCheck
      const checkResponse = await adminAuthCheck(loginValue);
      const checkData = checkResponse.data || {};
      updateFlow({
        login: checkData.login || loginValue,
        phone: checkData.phone || '',
        phoneMasked: checkData.phone_masked || '',
        emailMasked: checkData.email_masked || '',
        hasPhone: Boolean(checkData.has_phone),
        mergeOffer: null,
      });
      if (checkData.next === 'password') {
        updateFlow({ step: STEPS.PASSWORD, password: '' });
        return;
      }
      if (checkData.next === 'telegram') {
        await startTelegram(checkData.login || loginValue, checkData.phone);
        return;
      }
      if (checkData.next === 'phone_required') {
        setError(t('authPhoneRequired'));
        return;
      }
      setHint(t('authMergeSuccess'));
    } catch (err) {
      if (isNotAdminMergeOffer(err)) {
        const offer = normalizeMergeOffer(err.response.data);
        updateFlow({ step: STEPS.CHECK, mergeOffer: offer });
        setError(offer.message || getAdminAuthError(err, t('authMergeError')));
      } else {
        fail(err, 'authMergeError');
      }
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordLogin = async () => {
    if (!flow.password) {
      setError(t('authNeedPassword'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthLogin(flow.login, flow.password);
      finishSession(response.data);
    } catch (err) {
      fail(err, 'authLoginError');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = String(flow.code || '').trim();
    if (!code || code.length < (flow.codeLength || 4)) {
      setError(t('authNeedCode'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = {
        phone: flow.phone,
        request_id: flow.requestId,
        code,
        login: flow.login,
      };
      const response = flow.channel === 'sms'
        ? await adminAuthSmsVerify(payload)
        : await adminAuthTelegramVerify(payload);
      const data = response.data || {};
      if (isAdminSession(data)) {
        finishSession(data);
        return;
      }
      if (data.needs_setup || data.verify_token) {
        clearResendTimer();
        setResendLeft(0);
        clearEmailResendTimer();
        setEmailResendLeft(0);
        updateFlow({
          step: STEPS.SETUP,
          verifyToken: data.verify_token || '',
          needsEmail: Boolean(data.needs_email),
          emailMasked: data.email_masked || flow.emailMasked,
          phone: data.phone || flow.phone,
          password: '',
          confirmPassword: '',
          email: '',
          emailCode: '',
        });
        setHint(data.needs_email ? t('authSetupNeedEmail') : t('authSetupNeedPassword'));
        return;
      }
      setError(t('unknownError'));
    } catch (err) {
      fail(err, 'authCodeInvalid');
    } finally {
      setBusy(false);
    }
  };

  const handleResendCode = async () => {
    if (resendLeft > 0 || busy) return;
    setBusy(true);
    setError('');
    try {
      const payload = { login: flow.login, phone: flow.phone };
      const response = flow.channel === 'sms'
        ? await adminAuthSmsSend(payload)
        : await adminAuthTelegramSend(payload);
      applySendPayload(response.data || {}, flow.channel);
    } catch (err) {
      fail(err, 'authResendError');
    } finally {
      setBusy(false);
    }
  };

  const handleSwitchToSms = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthSmsSend({ login: flow.login, phone: flow.phone });
      applySendPayload(response.data || {}, 'sms');
    } catch (err) {
      fail(err, 'authSmsError');
    } finally {
      setBusy(false);
    }
  };

  const handleSendEmailCode = async () => {
    if (!flow.email.trim()) {
      setError(t('authNeedEmail'));
      return;
    }
    if (emailResendLeft > 0 || busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthEmailSendCode(flow.email.trim());
      const data = response.data || {};
      startEmailResendCountdown(data.resend_after || 60);
      setHint(
        data.mock && data.code
          ? t('authMockCode', { code: data.code })
          : t('authEmailCodeSent')
      );
    } catch (err) {
      fail(err, 'authEmailSendError', { email: true });
    } finally {
      setBusy(false);
    }
  };

  const handleCompleteSetup = async () => {
    if (!flow.password || flow.password.length < 8) {
      setError(t('authPasswordShort'));
      return;
    }
    if (flow.password !== flow.confirmPassword) {
      setError(t('authPasswordsMismatch'));
      return;
    }
    if (flow.needsEmail) {
      if (!flow.email.trim()) {
        setError(t('authNeedEmail'));
        return;
      }
      if (!flow.emailCode.trim()) {
        setError(t('authNeedEmailCode'));
        return;
      }
    }
    setBusy(true);
    setError('');
    try {
      const payload = {
        phone: flow.phone,
        verify_token: flow.verifyToken,
        password: flow.password,
        confirmPassword: flow.confirmPassword,
      };
      if (flow.needsEmail) {
        payload.email = flow.email.trim();
        payload.email_code = flow.emailCode.trim();
      }
      const response = await adminAuthComplete(payload);
      finishSession(response.data);
    } catch (err) {
      fail(err, 'authSetupError');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotStart = async () => {
    const loginValue = flow.login.trim();
    if (!loginValue) {
      setError(t('authNeedLogin'));
      return;
    }
    setBusy(true);
    setError('');
    setHint('');
    try {
      const response = await adminAuthForgot(loginValue);
      const data = response.data || {};
      updateFlow({
        step: STEPS.FORGOT_CODE,
        channel: 'email',
        emailMasked: data.email_masked || '',
        phoneMasked: data.phone_masked || '',
        hasPhone: Boolean(data.has_phone),
        codeLength: Number(data.code_length) || 6,
        code: '',
        requestId: '',
      });
      startResendCountdown(data.resend_after || 60);
      setHint(t('authForgotEmailSent', { email: data.email_masked || '' }));
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === 'email_required') {
        try {
          const smsResponse = await adminAuthForgotSms({ login: loginValue });
          const data = smsResponse.data || {};
          updateFlow({
            step: STEPS.FORGOT_CODE,
            channel: 'sms',
            phone: data.phone || flow.phone,
            phoneMasked: data.phone_masked || data.phone || '',
            hasPhone: true,
            requestId: data.request_id || '',
            codeLength: Number(data.code_length) || 4,
            code: '',
            mockCode: data.mock && data.code ? String(data.code) : '',
          });
          startResendCountdown(data.resend_after || 60);
          setHint(
            data.mock && data.code
              ? t('authMockCode', { code: data.code })
              : t('authForgotSmsSent')
          );
          setBusy(false);
          return;
        } catch (smsErr) {
          fail(smsErr, 'authForgotError');
          setBusy(false);
          return;
        }
      }
      fail(err, 'authForgotError');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotSms = async () => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthForgotSms({ login: flow.login, phone: flow.phone });
      const data = response.data || {};
      updateFlow({
        channel: 'sms',
        phone: data.phone || flow.phone,
        phoneMasked: data.phone_masked || data.phone || flow.phoneMasked,
        requestId: data.request_id || '',
        codeLength: Number(data.code_length) || 4,
        code: '',
        mockCode: data.mock && data.code ? String(data.code) : '',
      });
      startResendCountdown(data.resend_after || 60);
      setHint(
        data.mock && data.code
          ? t('authMockCode', { code: data.code })
          : t('authForgotSmsSent')
      );
    } catch (err) {
      fail(err, 'authSmsError');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotVerify = async () => {
    const code = String(flow.code || '').trim();
    if (!code) {
      setError(t('authNeedCode'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const payload = flow.channel === 'sms'
        ? {
            channel: 'sms',
            phone: flow.phone,
            request_id: flow.requestId,
            code,
            login: flow.login,
          }
        : {
            channel: 'email',
            login: flow.login,
            code,
          };
      const response = await adminAuthForgotVerify(payload);
      const data = response.data || {};
      updateFlow({
        step: STEPS.FORGOT_RESET,
        resetToken: data.reset_token || '',
        password: '',
        confirmPassword: '',
      });
      setHint(t('authForgotSetPassword'));
    } catch (err) {
      fail(err, 'authCodeInvalid');
    } finally {
      setBusy(false);
    }
  };

  const handleForgotReset = async () => {
    if (!flow.password || flow.password.length < 8) {
      setError(t('authPasswordShort'));
      return;
    }
    if (flow.password !== flow.confirmPassword) {
      setError(t('authPasswordsMismatch'));
      return;
    }
    setBusy(true);
    setError('');
    try {
      const response = await adminAuthForgotReset({
        reset_token: flow.resetToken,
        password: flow.password,
        confirmPassword: flow.confirmPassword,
      });
      finishSession(response.data);
    } catch (err) {
      fail(err, 'authSetupError');
    } finally {
      setBusy(false);
    }
  };

  const goBack = () => {
    setError('');
    setHint('');
    if (flow.step === STEPS.PASSWORD || flow.step === STEPS.CODE || flow.step === STEPS.FORGOT) {
      updateFlow({ step: STEPS.CHECK, password: '', code: '', mergeOffer: null });
      clearResendTimer();
      setResendLeft(0);
      return;
    }
    if (flow.step === STEPS.MERGE) {
      updateFlow({ step: STEPS.CHECK });
      return;
    }
    if (flow.step === STEPS.SETUP) {
      updateFlow({ step: STEPS.CODE });
      return;
    }
    if (flow.step === STEPS.FORGOT_CODE) {
      updateFlow({ step: STEPS.PASSWORD });
      return;
    }
    if (flow.step === STEPS.FORGOT_RESET) {
      updateFlow({ step: STEPS.FORGOT_CODE });
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    if (busy) return;
    if (flow.step === STEPS.CHECK) handleCheck();
    else if (flow.step === STEPS.PASSWORD) handlePasswordLogin();
    else if (flow.step === STEPS.CODE) handleVerifyCode();
    else if (flow.step === STEPS.SETUP) handleCompleteSetup();
    else if (flow.step === STEPS.FORGOT) handleForgotStart();
    else if (flow.step === STEPS.FORGOT_CODE) handleForgotVerify();
    else if (flow.step === STEPS.FORGOT_RESET) handleForgotReset();
    else if (flow.step === STEPS.MERGE) handleMergeConfirm();
  };

  const titleByStep = {
    [STEPS.CHECK]: t('authTitle'),
    [STEPS.PASSWORD]: t('authTitle'),
    [STEPS.CODE]: flow.channel === 'sms' ? t('authTitleSms') : t('authTitleTelegram'),
    [STEPS.SETUP]: t('authTitleSetup'),
    [STEPS.FORGOT]: t('authTitleForgot'),
    [STEPS.FORGOT_CODE]: t('authTitleForgotCode'),
    [STEPS.FORGOT_RESET]: t('authTitleForgotReset'),
    [STEPS.MERGE]: t('authMergeTitle'),
  };

  const submitLabel = {
    [STEPS.CHECK]: t('authContinue'),
    [STEPS.PASSWORD]: t('login'),
    [STEPS.CODE]: t('authConfirmCode'),
    [STEPS.SETUP]: t('authSavePassword'),
    [STEPS.FORGOT]: t('authSendReset'),
    [STEPS.FORGOT_CODE]: t('authConfirmCode'),
    [STEPS.FORGOT_RESET]: t('authSavePassword'),
    [STEPS.MERGE]: t('authMergeConfirm'),
  };

  const mergeOffer = flow.mergeOffer;

  return (
    <Modal isOpen={visible} onRequestClose={busy ? undefined : closeModal} style={customStyles}>
      <form className="authModal" onSubmit={onSubmit}>
        <div className="authModalHeader">{titleByStep[flow.step]}</div>

        {hint ? <p className="authModalHint">{hint}</p> : null}

        {(flow.step === STEPS.CHECK || flow.step === STEPS.FORGOT) && (
          <div className="authModalInputWrapper">
            <input
              type="text"
              className="authModalInput"
              value={flow.login}
              placeholder={t('loginPlaceholder')}
              autoComplete="username"
              autoFocus
              disabled={busy}
              onChange={(e) => {
                updateFlow({ login: e.target.value, mergeOffer: null });
                if (error) setError('');
              }}
            />
          </div>
        )}

        {flow.step === STEPS.MERGE && mergeOffer ? (
          <div className="authModalMergeBox">
            <p className="authModalHint">{mergeOffer.message || t('authMergeHint')}</p>
            {mergeOffer.phone_masked ? (
              <p className="authModalHint muted">{t('authMergePhone', { phone: mergeOffer.phone_masked })}</p>
            ) : null}
            {mergeOffer.keep_id != null ? (
              <p className="authModalHint muted">{t('authMergeKeep', { id: mergeOffer.keep_id })}</p>
            ) : null}
            {mergeOffer.kill_ids?.length ? (
              <p className="authModalHint muted">
                {t('authMergeKill', { ids: mergeOffer.kill_ids.join(', ') })}
              </p>
            ) : null}
            {mergeOffer.kill_emails_masked?.length ? (
              <p className="authModalHint muted">
                {t('authMergeEmails', { emails: mergeOffer.kill_emails_masked.join(', ') })}
              </p>
            ) : null}
          </div>
        ) : null}

        {flow.step === STEPS.PASSWORD && (
          <>
            <input
              type="text"
              name="username"
              className="authModalUsernameMirror"
              value={flow.login}
              autoComplete="username"
              readOnly
              tabIndex={-1}
              aria-hidden="true"
            />
            <p className="authModalHint muted">{flow.login}</p>
            <div className="authModalInputWrapper">
              <input
                type="password"
                name="password"
                className="authModalInput"
                value={flow.password}
                placeholder={t('passwordPlaceholder')}
                autoComplete="current-password"
                autoFocus
                disabled={busy}
                onChange={(e) => updateFlow({ password: e.target.value })}
              />
            </div>
          </>
        )}

        {(flow.step === STEPS.CODE || flow.step === STEPS.FORGOT_CODE) && (
          <>
            {(flow.phoneMasked || flow.emailMasked || flow.phone) ? (
              <p className="authModalHint muted">
                {flow.channel === 'email'
                  ? (flow.emailMasked || flow.login)
                  : (flow.phoneMasked || flow.phone)}
              </p>
            ) : null}
            <div className="authModalInputWrapper">
              <input
                type="text"
                inputMode="numeric"
                className="authModalInput authModalCodeInput"
                value={flow.code}
                placeholder={t('authCodePlaceholder', { n: flow.codeLength || 6 })}
                autoComplete="one-time-code"
                autoFocus
                maxLength={Math.max(flow.codeLength || 6, 8)}
                disabled={busy}
                onChange={(e) => updateFlow({ code: e.target.value.replace(/\D/g, '') })}
              />
            </div>
          </>
        )}

        {(flow.step === STEPS.SETUP || flow.step === STEPS.FORGOT_RESET) && (
          <>
            {flow.step === STEPS.SETUP && flow.needsEmail ? (
              <>
                <div className="authModalInputWrapper">
                  <input
                    type="email"
                    name="username"
                    className="authModalInput"
                    value={flow.email}
                    placeholder={t('authEmailPlaceholder')}
                    autoComplete="username"
                    disabled={busy}
                    onChange={(e) => updateFlow({ email: e.target.value })}
                  />
                </div>
                <div className="authModalRow">
                  <input
                    type="text"
                    name="email_code"
                    inputMode="numeric"
                    className="authModalInput"
                    value={flow.emailCode}
                    placeholder={t('authEmailCodePlaceholder')}
                    autoComplete="one-time-code"
                    disabled={busy}
                    onChange={(e) => updateFlow({ emailCode: e.target.value.replace(/\D/g, '') })}
                  />
                  <button
                    type="button"
                    className="authModalSecondaryBtn"
                    disabled={busy || emailResendLeft > 0}
                    onClick={handleSendEmailCode}
                  >
                    {emailResendLeft > 0 ? t('authResendIn', { n: emailResendLeft }) : t('authSendEmailCode')}
                  </button>
                </div>
              </>
            ) : (
              <input
                type="text"
                name="username"
                className="authModalUsernameMirror"
                value={flow.login || flow.phone || ''}
                autoComplete="username"
                readOnly
                tabIndex={-1}
                aria-hidden="true"
              />
            )}
            <div className="authModalInputWrapper">
              <input
                type="password"
                name="new-password"
                className="authModalInput"
                value={flow.password}
                placeholder={t('authNewPasswordPlaceholder')}
                autoComplete="new-password"
                autoFocus={!flow.needsEmail}
                disabled={busy}
                onChange={(e) => updateFlow({ password: e.target.value })}
              />
            </div>
            <div className="authModalInputWrapper">
              <input
                type="password"
                name="new-password-confirm"
                className="authModalInput"
                value={flow.confirmPassword}
                placeholder={t('authConfirmPasswordPlaceholder')}
                autoComplete="new-password"
                disabled={busy}
                onChange={(e) => updateFlow({ confirmPassword: e.target.value })}
              />
            </div>
          </>
        )}

        <div className="authModalButtonWrapper">
          <button type="submit" className="authModalButton" disabled={busy}>
            <span className="authModalButtonText">
              {busy ? t('authLoading') : submitLabel[flow.step]}
            </span>
          </button>
        </div>

        <div className="authModalLinks">
          {flow.step !== STEPS.CHECK ? (
            <button type="button" className="authModalLinkBtn" disabled={busy} onClick={goBack}>
              {t('authBack')}
            </button>
          ) : null}

          {flow.step === STEPS.PASSWORD ? (
            <button
              type="button"
              className="authModalLinkBtn"
              disabled={busy}
              onClick={() => {
                setError('');
                setHint('');
                handleForgotStart();
              }}
            >
              {t('authForgotPassword')}
            </button>
          ) : null}

          {flow.step === STEPS.CODE && flow.channel === 'telegram' ? (
            <button type="button" className="authModalLinkBtn" disabled={busy} onClick={handleSwitchToSms}>
              {t('authSendSmsInstead')}
            </button>
          ) : null}

          {(flow.step === STEPS.CODE || (flow.step === STEPS.FORGOT_CODE && flow.channel === 'sms')) ? (
            <button
              type="button"
              className="authModalLinkBtn"
              disabled={busy || resendLeft > 0}
              onClick={flow.step === STEPS.FORGOT_CODE ? handleForgotSms : handleResendCode}
            >
              {resendLeft > 0 ? t('authResendIn', { n: resendLeft }) : t('authResendCode')}
            </button>
          ) : null}

          {flow.step === STEPS.FORGOT_CODE && flow.channel === 'email' && flow.hasPhone ? (
            <button type="button" className="authModalLinkBtn" disabled={busy} onClick={handleForgotSms}>
              {t('authSendSmsInstead')}
            </button>
          ) : null}

          {flow.step === STEPS.FORGOT_CODE && flow.channel === 'email' ? (
            <button
              type="button"
              className="authModalLinkBtn"
              disabled={busy || resendLeft > 0}
              onClick={handleForgotStart}
            >
              {resendLeft > 0 ? t('authResendIn', { n: resendLeft }) : t('authResendCode')}
            </button>
          ) : null}
        </div>

        {flow.step === STEPS.CHECK || flow.step === STEPS.PASSWORD ? (
          <div className="authModalTermsWrapper">
            <p className="authModalTermsText">
              {t('authTermsPrefix')}{' '}
              <span className="authModalTermsLink" onClick={openDocuments}>
                {t('authTermsLink')}
              </span>
            </p>
          </div>
        ) : null}

        {error ? (
          <div className="authModalErrorTextWrapper">
            <div className="authModalErrorText">{error}</div>
          </div>
        ) : null}

        {flow.step === STEPS.CHECK && mergeOffer?.can_merge ? (
          <button
            type="button"
            className="authModalMergeBtn"
            disabled={busy}
            onClick={handleMergePreview}
          >
            {t('authMergeAccounts')}
          </button>
        ) : null}
      </form>
    </Modal>
  );
}

export default AuthModal;
